import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Switch from '@/components/Inputs/Switch';
import TaskAvatar from '@/components/Tasks/TaskAvatar';
import TaskAttachment from '@/components/Tasks/TaskAttachment';
import TaskFavoriteHeart from '@/components/Tasks/TaskFavoriteHeart';
import {
  useChatStore,
  useInferenceTasksStore,
  useModalStore,
  useToastStore,
} from '@/store';
import {
  taskScheduleAccent,
  taskScheduleIcon,
  taskScheduleHeadline,
  formatRelative,
} from '@/utils/tasks_catalog';

// "Calendar tile" task card. A soft cadence-tinted band across the top
// reads "DAILY · 8:00 AM"; title + meta sit on a clean body below. Used by
// the /tasks page (and any category route) to render saved tasks. Behaviors
// (run-now, toggle, view results, favorite, edit) match the legacy TaskCard.

function TaskCardCalendar({ task, onPress, showActions = false }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isXS, isSM, isMD } = useResponsiveness();
  const router = useRouter();

  const { openModal } = useModalStore(s => s.actions);
  const { addToast } = useToastStore(s => s.actions);
  const toggleTask = useInferenceTasksStore(s => s.toggleTask);
  const runTaskNow = useInferenceTasksStore(s => s.runTaskNow);
  const fetchChats = useChatStore(s => s.fetchChats);

  const [isToggling, setIsToggling] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const accent = taskScheduleAccent(task);
  const enabled = task?.flags?.isEnabled ?? true;
  const hasConversation = !!(task?.conversationId || task?.conversation?.id);

  const styles = useMemo(
    () => createStyles(colors, accent, enabled, isXS, isSM, isMD),
    [colors, accent, enabled, isXS, isSM, isMD]
  );

  const handlePress = useCallback(() => {
    if (onPress) onPress(task);
    else openModal('CreateTaskModal', { task });
  }, [onPress, openModal, task]);

  const handleToggle = useCallback(async (next) => {
    if (isToggling) return;
    try {
      setIsToggling(true);
      await toggleTask(task.id, next);
      addToast(next ? t('tasks.enabled') : t('tasks.paused'), 'success');
    } catch (err) {
      addToast(err?.message || t('tasks.updateFailed'), 'error');
    } finally {
      setIsToggling(false);
    }
  }, [task?.id, isToggling, toggleTask, addToast, t]);

  const handleRun = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (isRunning) return;
    try {
      setIsRunning(true);
      const result = await runTaskNow(task.id);
      const conversationId = result?.conversationId;
      if (conversationId) {
        router.push(`/c/${conversationId}`);
      } else {
        addToast(t('tasks.started'), 'success');
        setTimeout(() => fetchChats(1), 2000);
      }
    } catch (err) {
      addToast(err?.message || t('tasks.runFailed'), 'error');
    } finally {
      setIsRunning(false);
    }
  }, [task?.id, isRunning, runTaskNow, addToast, fetchChats, t, router]);

  const handleViewResults = useCallback((e) => {
    e?.stopPropagation?.();
    const id = task?.conversationId || task?.conversation?.id;
    if (id) router.push(`/c/${id}`);
  }, [task, router]);

  return (
    <Pressable
      onPress={handlePress}
      style={({ hovered }) => [styles.card, hovered && styles.cardHovered]}
    >
      <View style={styles.headerBand}>
        <View style={styles.headerLeft}>
          <Icon name={taskScheduleIcon(task)} size={14} color={accent} />
          <Text size="xs" weight="extraBold" style={{ color: accent, letterSpacing: 0.8 }} numberOfLines={1}>
            {taskScheduleHeadline(task)}
          </Text>
        </View>
        <View style={styles.headerRight}>
          <View style={[styles.statusDot, { backgroundColor: enabled ? '#16A34A' : colors.text_color_tertiary }]} />
          <Text size="xs" weight="bold" color={enabled ? 'primary' : 'secondary'}>
            {enabled ? t('tasks.enabledStatus') : t('tasks.pausedStatus')}
          </Text>
          <TaskFavoriteHeart task={task} />
        </View>
      </View>

      <View style={styles.body}>
        <View style={styles.titleRow}>
          <TaskAvatar task={task} size={40} />
          <Text size="m" weight="bold" numberOfLines={2} style={{ flex: 1, minWidth: 0 }}>
            {task?.name || t('tasks.untitled')}
          </Text>
        </View>

        <TaskAttachment task={task} />

        <View style={styles.metaRow}>
          {!!task?.nextRunAt && (
            <View style={styles.metaItem}>
              <Icon name="circle-arrow-right" size={11} color={colors.text_color_tertiary} />
              <Text size="xs" color="tertiary">Next {formatRelative(task.nextRunAt)}</Text>
            </View>
          )}
          {!!task?.lastRunAt && (
            <View style={styles.metaItem}>
              <Icon name="circle-check" size={11} color={colors.text_color_tertiary} />
              <Text size="xs" color="tertiary">Last {formatRelative(task.lastRunAt)}</Text>
            </View>
          )}
          {task?.emailDelivery?.isEnabled && (
            <View style={styles.metaItem}>
              <Icon name="envelope" size={11} color={colors.text_color_tertiary} />
              <Text size="xs" color="tertiary">{t('tasks.email')}</Text>
            </View>
          )}

        </View>

        {showActions && (
          <View style={styles.actions}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Pressable
                onPress={handleRun}
                disabled={isRunning}
                style={({ hovered }) => [
                  styles.runBtn,
                  hovered && { backgroundColor: colors.solid_color_tertiary, borderColor: colors.border_color_primary },
                  isRunning && { opacity: 0.6 },
                ]}
              >
                {isRunning
                  ? <ActivityIndicator size={10} color={colors.text_color_secondary} />
                  : <Icon name="play" size={10} color={colors.text_color_secondary} solid />}
                <Text size="xs" weight="bold" color="secondary">
                  {isRunning ? t('tasks.running') : t('tasks.runNow')}
                </Text>
              </Pressable>
              {hasConversation && (
                <Pressable onPress={handleViewResults} style={({ hovered }) => [styles.linkBtn, hovered && { backgroundColor: colors.solid_color_tertiary }]}>
                  <Icon name="arrow-up-right-from-square" size={10} color={colors.text_color_secondary} />
                  <Text size="xs" color="secondary">{t('tasks.results')}</Text>
                </Pressable>
              )}
            </View>
            <View onClick={(e) => e.stopPropagation()}>
              <Switch
                value={enabled}
                onValueChange={handleToggle}
                disabled={isToggling}
                accessibilityLabel={`${t('tasks.enabledStatus')}: ${task?.name || t('tasks.untitled')}`}
              />
            </View>
          </View>
        )}
      </View>
    </Pressable>
  );
}

export function TaskCardCalendarPlaceholder() {
  const { colors } = useTheme();
  const { isXS, isSM, isMD } = useResponsiveness();
  const styles = useMemo(
    () => createStyles(colors, '#9333EA', true, isXS, isSM, isMD),
    [colors, isXS, isSM, isMD]
  );
  return (
    <View style={styles.card}>
      <View style={styles.headerBand}>
        <View style={[styles.placeholderBar, { width: 90 }]} />
        <View style={[styles.placeholderBar, { width: 50 }]} />
      </View>
      <View style={styles.body}>
        <View style={styles.titleRow}>
          <View style={[styles.glyph, { opacity: 0.5 }]} />
          <View style={{ flex: 1, gap: 6 }}>
            <View style={[styles.placeholderBar, { width: '70%', height: 12 }]} />
            <View style={[styles.placeholderBar, { width: '40%', height: 10 }]} />
          </View>
        </View>
      </View>
    </View>
  );
}

const createStyles = (colors, accent, enabled, isXS, isSM, isMD) => {
  // Compact tightens padding and trims the glyph on tablet (isMD) and
  // below so the card body still fits two lines plus the meta row in a
  // 1-up column on mobile.
  const compact = isSM;
  const bodyPad = isXS ? 10 : compact ? 12 : 14;
  const cardMinH = isXS ? 130 : compact ? 140 : 150;

  return StyleSheet.create({
  card: {
    minHeight: cardMinH,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  cardHovered: {
    borderColor: colors.border_color_primary,
    backgroundColor: colors.solid_color_tertiary,
  },
  headerBand: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: compact ? 10 : 12,
    paddingVertical: compact ? 6 : 8,
    backgroundColor: enabled ? `${accent}12` : colors.solid_color_secondary,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border_color_secondary,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  body: {
    padding: bodyPad,
    gap: compact ? 8 : 10,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    paddingTop: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border_color_secondary,
  },
  runBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 10, paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: colors.solid_color_secondary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  linkBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 8, paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: colors.solid_color_secondary,
  },
  placeholderBar: {
    height: 10,
    backgroundColor: colors.solid_color_secondary,
    borderRadius: 4,
  },
  });
};

export default React.memo(TaskCardCalendar);
